import { Link, useLocation } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Divider,
  useTheme,
  alpha,
} from "@mui/material";

import Footer from "./Footer";
import { useEffect } from "react";
import ScrollToTop from "./ScrollToTop";

const PrivacyPolicy = () => {
  const theme = useTheme();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const sections = [
    {
      title: "Links To Other Websites",
      content:
        "This website contains links to other websites such as Facebook, LinkedIn and others that are not owned or controlled by us. Please be aware that Kurdish Translation Services 4U Ltd is not responsible or liable for the privacy policies and practices of such other Sites. This privacy policy applies only to information collected by this website.",
    },
    {
      title: "Changes",
      content:
        "We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on the Site. Please check our website frequently to see any updates to our policy.",
    },
    {
      title: "Information Collection And Use",
      content:
        "We use your personal information to ensure that we provide the very best possible service. We may request specific personally identifying information from you while you use our Site so that we can get in touch with or identify you. Your name is one example of personally identifiable information, but it is not the only one (personal information).",
    },
    {
      title: "Security",
      content:
        "Ensuring the accuracy and currency of the personal information we collect about you is important. If your personal information has changed during the duration of your connection with us, kindly get in contact. We care about the security of your personal information. We have put in place sufficient physical, technological, and administrative systems to safeguard and secure the information we collect in order to prevent unauthorized access or disclosure.\n\nWe will email your work as Word or PDF document as it is completed. You don't receive a physical copy from us. This is because of security concerns, which will cause a delay in the process and increase the chance of mail loss.",
    },
    {
      title: "Governing Law",
      content:
        "Use of this website and any dispute arising out of such use will be governed by the laws of the United Kingdom.\n\nIf you have any questions about these privacy policies, please contact us.",
    },
  ];

  return (
    <Box sx={{ minHeight: "100vh" }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          py: { xs: 6, md: 10 },
          mt: { xs: 7, sm: 8 },
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)",
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: 32, md: 48 },
              fontWeight: 700,
              color: "white",
              textAlign: "center",
              mb: 3,
              textShadow: "0 2px 10px rgba(0,0,0,0.2)",
            }}
          >
            Privacy Policy
          </Typography>
        </Container>
      </Box>

      {/* Main Content */}
      <Container
        maxWidth="lg"
        sx={{
          mt: { xs: 4, md: 8 },
          mb: { xs: 4, md: 8 },
          px: { xs: 2, md: 4 },
        }}
      >
        <Stack spacing={{ xs: 2, md: 4 }}>
          {/* Introduction */}
          <Paper
            elevation={0}
            sx={{
              padding: { xs: 3, md: 5 },
              borderRadius: 4,
              background:
                "linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)",
              border: "1px solid",
              borderColor: alpha(theme.palette.primary.main, 0.1),
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: 18, md: 20 },
                lineHeight: { xs: 1.6, md: 1.8 },
                textAlign: "justify",
                color: "text.primary",
              }}
            >
              Kurdish Translation Services 4U Ltd (us, we, or our), operates{" "}
              <Link
                to="/"
                style={{
                  color: theme.palette.primary.main,
                  textDecoration: "none",
                  fontWeight: 600,
                }}
              >
                www.kurdishtranslationservices4u.co.uk
              </Link>
              . This page informs you of our policies. By using our website you
              agree to be bound by these policies. Before using the Site, it is
              important to read this statement carefully.
            </Typography>
          </Paper>

          {/* Policy Sections */}
          {sections.map((section, index) => (
            <Paper
              key={index}
              elevation={0}
              sx={{
                padding: { xs: 3, md: 5 },
                borderRadius: 4,
                background:
                  "linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)",
                border: "1px solid",
                borderColor: alpha(theme.palette.primary.main, 0.1),
                transition: "all 0.3s ease",
                // "&:hover": {
                //   borderColor: alpha(theme.palette.primary.main, 0.3),
                //   transform: "translateY(-2px)",
                //   boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                // },
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontSize: { xs: 24, md: 28 },
                  fontWeight: 700,
                  mb: { xs: 1.5, md: 2 },
                  color: "primary.main",
                }}
              >
                {index + 1}- {section.title}
              </Typography>
              <Divider
                sx={{
                  mb: { xs: 2, md: 3 },
                  borderColor: alpha(theme.palette.primary.main, 0.2),
                }}
              />
              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: 16, md: 18 },
                  lineHeight: { xs: 1.6, md: 1.8 },
                  textAlign: "justify",
                  color: "text.primary",
                  whiteSpace: "pre-line",
                }}
              >
                {section.content}
              </Typography>
            </Paper>
          ))}
        </Stack>
      </Container>

      {/* Scroll to Top Button */}
      <ScrollToTop />

      <Footer />
    </Box>
  );
};

export default PrivacyPolicy;
